import { StrictMode, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import '@tanstack/devtools';
import '@tanstack/react-query-devtools';

import App from './app/app';
import { initObservability } from '@shared/observability';
import { installGlobalErrorCapture } from '@shared/lib/debug-log';

import 'animate.css';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

import './index.css';
import 'virtual:pwa-theme.css';

/**
 * Dev-only, deliberately. `StrictMode` renders no DOM of its own and is
 * effectively inert in a production build, so wrapping unconditionally would
 * also be defensible -- but every one of these ten templates is a live
 * publisher app, and gating on `import.meta.env.DEV` means the tree that
 * ships is byte-for-byte what shipped before.
 *
 * What it buys in dev: React double-invokes component bodies, and mounts →
 * unmounts → remounts every component once, running effect cleanups in
 * between. That surfaces impure renders and missing/incorrect effect teardown
 * at development time instead of as a slow leak on a reader's phone.
 */
const Root = ({ children }: { children: ReactNode }) =>
	import.meta.env.DEV ? <StrictMode>{children}</StrictMode> : children;

initObservability();
installGlobalErrorCapture();

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
	<Root>
		<App />
	</Root>,
);
